import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Check, X, ChevronLeft, ChevronRight, User, Mail, Phone, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function BookingSystem() {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [bookings, setBookings] = useState([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Services offered
  const services = [
    {
      id: 'deep-tissue',
      name: 'Deep Tissue Massage',
      duration: '60 mins',
      price: '£50',
      description: 'Focused pressure to release chronic muscle tension'
    },
    {
      id: 'swedish',
      name: 'Swedish Massage',
      duration: '60 mins',
      price: '£45',
      description: 'Classic relaxation massage with flowing strokes'
    },
    {
      id: 'lymphatic',
      name: 'Lymphatic Drainage Massage',
      duration: '60 mins',
      price: '£55',
      description: 'Gentle massage to stimulate lymphatic system'
    }
  ];

  // Load bookings from localStorage
  useEffect(() => {
    const savedBookings = localStorage.getItem('hollisBookings');
    if (savedBookings) {
      setBookings(JSON.parse(savedBookings));
    }
  }, []);

  // Save bookings to localStorage
  useEffect(() => {
    if (bookings.length > 0) {
      localStorage.setItem('hollisBookings', JSON.stringify(bookings));
    }
  }, [bookings]);

  // Get available time slots based on day of week
  const getAvailableSlots = (date) => {
    const dayOfWeek = date.getDay();
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return [];

    let slots = [];

    // Monday to Thursday: 5:30pm - 7pm
    if (dayOfWeek >= 1 && dayOfWeek <= 4) {
      slots = ['17:30', '18:00', '18:30'];
    }
    // Friday: 3pm - 7pm
    else if (dayOfWeek === 5) {
      slots = ['15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30'];
    }
    // Saturday: 9am - 5pm
    else if (dayOfWeek === 6) {
      slots = ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30',
               '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'];
    }
    else {
      slots = [];
    }

    const dateString = date.toISOString().split('T')[0];
    const bookedSlots = bookings
      .filter(booking => booking.date === dateString && booking.status !== 'cancelled')
      .map(booking => booking.time);
    
    return slots.filter(slot => !bookedSlots.includes(slot));
  };

  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isDateAvailable = (date) => {
    if (!date) return false;
    const slots = getAvailableSlots(date);
    return slots.length > 0;
  };

  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const handleBooking = () => {
    const newBooking = {
      id: Date.now().toString(),
      date: selectedDate.toISOString().split('T')[0],
      time: selectedTime,
      service: selectedService,
      customer: customerDetails,
      status: 'pending',
      createdAt: new Date().toISOString()
    };

    setBookings([...bookings, newBooking]);
    setCurrentStep(5);
  };

  const resetForm = () => {
    setCurrentStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedService(null);
    setCustomerDetails({ name: '', email: '', phone: '', notes: '' });
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    const maxMonth = new Date();
    maxMonth.setMonth(maxMonth.getMonth() + 2);
    if (currentMonth < maxMonth) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    }
  };

  // Admin Panel Component
  if (showAdminPanel) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold text-gray-900">Admin Panel - All Bookings</h2>
              <button
                onClick={() => setShowAdminPanel(false)}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
              >
                Back to Booking
              </button>
            </div>

            {bookings.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No bookings yet</p>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => {
                  const service = services.find(s => s.id === booking.service);
                  return (
                    <div key={booking.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold text-lg text-gray-900">{booking.customer.name}</h3>
                          <p className="text-gray-600">
                            📅 {new Date(booking.date).toLocaleDateString('en-GB', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </p>
                          <p className="text-gray-600">⏰ {formatTime(booking.time)}</p>
                          <p className="text-gray-600">💆 {service?.name} - {service?.price}</p>
                          <p className="text-gray-600">📧 {booking.customer.email}</p>
                          <p className="text-gray-600">📱 {booking.customer.phone}</p>
                          {booking.customer.notes && (
                            <p className="text-gray-600 mt-2">📝 Notes: {booking.customer.notes}</p>
                          )}
                        </div>
                        <div>
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {booking.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Back to Home Button */}
        <Link 
          to="/" 
          className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-6 font-semibold"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Book Your Massage</h1>
          <p className="text-gray-600">Select your preferred date, time, and service</p>
          
          {/* Admin Access Button */}
          <button
            onClick={() => setShowAdminPanel(true)}
            className="mt-4 text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Admin: View All Bookings
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  currentStep >= step ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {step}
                </div>
                {step < 4 && (
                  <div className={`w-12 h-1 ${currentStep > step ? 'bg-teal-600' : 'bg-gray-200'}`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Booking Card */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          
          {/* Step 1: Select Date */}
          {currentStep === 1 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Select a Date</h2>
              
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={previousMonth}
                  disabled={currentMonth.getMonth() === new Date().getMonth()}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30"
                >
                  <ChevronLeft size={24} />
                </button>
                <h3 className="text-xl font-bold">
                  {currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                </h3>
                <button
                  onClick={nextMonth}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <ChevronRight size={24} />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center font-semibold text-gray-600 py-2">
                    {day}
                  </div>
                ))}
                {getCalendarDays().map((date, index) => {
                  const isAvailable = isDateAvailable(date);
                  const isSelected = selectedDate && date && 
                    selectedDate.toDateString() === date.toDateString();
                  
                  return (
                    <button
                      key={index}
                      disabled={!date || !isAvailable}
                      onClick={() => date && isAvailable && setSelectedDate(date)}
                      className={`py-3 rounded-lg font-semibold transition ${
                        !date ? 'invisible' :
                        isSelected ? 'bg-teal-600 text-white' :
                        isAvailable ? 'bg-gray-100 hover:bg-teal-600 hover:text-white' :
                        'bg-gray-50 text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      {date ? date.getDate() : ''}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={!selectedDate}
                  className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next: Select Time
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Select Time */}
          {currentStep === 2 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Select a Time</h2>
              <p className="text-gray-600 mb-6">
                {selectedDate?.toLocaleDateString('en-GB', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>

              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {getAvailableSlots(selectedDate).map((time) => (
                  <button
                    key={time}
                    onClick={() => setSelectedTime(time)}
                    className={`py-3 px-4 rounded-lg font-semibold transition ${
                      selectedTime === time
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 hover:bg-teal-600 hover:text-white'
                    }`}
                  >
                    {formatTime(time)}
                  </button>
                ))}
              </div>

              <div className="mt-6 flex justify-between">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                >
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  disabled={!selectedTime}
                  className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition disabled:opacity-50"
                >
                  Next: Select Service
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Select Service */}
          {currentStep === 3 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Select a Service</h2>

              <div className="space-y-4">
                {services.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => setSelectedService(service.id)}
                    className={`w-full text-left p-6 rounded-lg border-2 transition ${
                      selectedService === service.id
                        ? 'border-teal-600 bg-blue-50'
                        : 'border-gray-200 hover:border-teal-600'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{service.name}</h3>
                        <p className="text-gray-600 mt-1">{service.description}</p>
                        <p className="text-gray-600 mt-2">⏱️ {service.duration}</p>
                      </div>
                      <div className="text-2xl font-bold text-teal-600">{service.price}</div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-6 flex justify-between">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                >
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(4)}
                  disabled={!selectedService}
                  className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition disabled:opacity-50"
                >
                  Next: Your Details
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Customer Details */}
          {currentStep === 4 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Details</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    <User className="inline mr-2" size={20} />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={customerDetails.name}
                    onChange={(e) => setCustomerDetails({...customerDetails, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-teal-600"
                    placeholder="John Smith"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    <Mail className="inline mr-2" size={20} />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={customerDetails.email}
                    onChange={(e) => setCustomerDetails({...customerDetails, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-teal-600"
                    placeholder="john@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    <Phone className="inline mr-2" size={20} />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={customerDetails.phone}
                    onChange={(e) => setCustomerDetails({...customerDetails, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-teal-600"
                    placeholder="07123 456789"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    value={customerDetails.notes}
                    onChange={(e) => setCustomerDetails({...customerDetails, notes: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-teal-600"
                    rows="3"
                    placeholder="Any special requirements or health conditions we should know about?"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-between">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                >
                  Back
                </button>
                <button
                  onClick={handleBooking}
                  disabled={!customerDetails.name || !customerDetails.email || !customerDetails.phone}
                  className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition disabled:opacity-50"
                >
                  Confirm Booking
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Confirmation */}
          {currentStep === 5 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="text-green-600" size={40} />
              </div>
              
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Booking Received!</h2>
              <p className="text-gray-600 mb-8">
                Thank you for your booking request. I'll contact you shortly to confirm your appointment.
              </p>

              <div className="bg-gray-50 rounded-lg p-6 text-left mb-8">
                <h3 className="font-bold text-lg mb-4">Booking Summary:</h3>
                <div className="space-y-2 text-gray-700">
                  <p>📅 <strong>Date:</strong> {selectedDate?.toLocaleDateString('en-GB', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</p>
                  <p>⏰ <strong>Time:</strong> {formatTime(selectedTime)}</p>
                  <p>💆 <strong>Service:</strong> {services.find(s => s.id === selectedService)?.name}</p>
                  <p>👤 <strong>Name:</strong> {customerDetails.name}</p>
                  <p>📧 <strong>Email:</strong> {customerDetails.email}</p>
                  <p>📱 <strong>Phone:</strong> {customerDetails.phone}</p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-yellow-800">
                  <strong>⚠️ Note:</strong> This booking is provisional. I will email you within 24 hours to confirm your appointment.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={resetForm}
                  className="bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-700 transition"
                >
                  Make Another Booking
                </button>
                <Link
                  to="/"
                  className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-300 transition text-center"
                >
                  Return to Home
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}